import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreatePerson } from "@factory/query/person.query";
import { PersonFormRule, PersonFormSchema } from "@factory/form/form.rule";
import { personFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewPerson() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreatePerson } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Person is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addPerson, isLoading: isCreating } =
    useQyCreatePerson(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<PersonFormSchema>({
    defaultValues: personFormDefault,
    resolver: zodResolver(PersonFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: PersonFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreatePerson(updatedForm);
    addPerson(formData);
  };
  const handleValidateError = (err: FieldErrors<PersonFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
